import type { ComponentProps } from 'react';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { BASE_PATHS, PATHS, formatMoney, formatNumber } from '@belong/common';
import {
  BulletList,
  Button,
  Form,
  Image,
  Modal,
  Spinner,
  Text,
  ButtonSelectorField,
  TextField,
  Link,
} from '@belong/ui';
import {
  ResidentLeadInterestUpdateModel,
  UnitListingDetailsModel,
} from 'src/api/models';
import {
  updateUserInterestSurveyService,
  validateInterestSurveyEntryService,
} from 'src/api/resident-lead';
import {
  getBathRoomString,
  getFormattedBeds,
} from 'src/features/listing-page/utils';
import { useFetchApplicationStatus } from '../../queries';
import { formatAddress } from '../../utils';

type Props = Pick<ComponentProps<typeof Modal>, 'isOpen' | 'onDismiss'> & {
  home: UnitListingDetailsModel;
};

export function TourSurveyModal({ home, isOpen, onDismiss }: Props) {
  const router = useRouter();
  const { query, asPath } = router;
  const { token, attendance, option } = query;
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const { applicationStatus } = useFetchApplicationStatus(token as string);

  const { basicInfo, address, listingInfo } = home;
  const path = new URL(asPath, 'http://example.com').pathname;

  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        await validateInterestSurveyEntryService(token as string);
        if (option) {
          // @ts-ignore
          await updateUserInterestSurveyService(token as string, {
            interestLevel:
              option as ResidentLeadInterestUpdateModel['interestLevel'],
          });
        }

        setIsValid(true);
      } catch (_e) {
        setIsValid(false);
      }
      setLoading(false);
    }

    init();
  }, [option, token]);

  async function handleSubmit(values: ResidentLeadInterestUpdateModel) {
    try {
      await updateUserInterestSurveyService(token as string, values);
    } catch {}

    if (values.interestLevel === 'Interested') {
      if (attendance === 'Attended') {
        if (applicationStatus === 'NoApplications') {
          return router.push({
            pathname: PATHS.RESIDENT_APPLICATION_GET_STARTED,
            query: { homeId: basicInfo?.unitId },
          });
        }

        return onDismiss();
      }

      return router.push({ pathname: path, query: { view: 'tours' } });
    }

    if (values.interestLevel === 'NotInterested') {
      if (values.noLongerInMarket === true) {
        return onDismiss();
      }

      return router.push(BASE_PATHS.HOME);
    }

    if (values.interestLevel === 'Neutral') {
      return onDismiss();
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      name="tour_modal"
      onDismiss={onDismiss}
      overlayClassName="bg-opacity-100"
    >
      {loading && <Spinner />}
      {!isValid && (
        <div className="pt-2xl">
          <div className="px-sm flex justify-center">
            <Text variant="h2" fontWeight="semibold" className="text-center">
              Hmm... looks like this link is no longer valid.
            </Text>
          </div>
          {/* TODO: Extract bottom bar!!!!! */}
          <div className="mt-sm px-2sm md:mt-xl md:border-dark-gray md:bg-light-gray md:p-md w-full md:flex md:justify-between md:border-0 md:border-t md:border-solid">
            <div className="mb-sm flex items-center justify-center md:mb-0 md:justify-start">
              <Text className="mr-xs md:mr-sm">Questions or concerns?</Text>
              <Link href={PATHS.SUPPORT}>{"LET'S TALK"}</Link>
            </div>
            <div>
              <Button onClick={onDismiss}>DONE</Button>
            </div>
          </div>
        </div>
      )}

      {isValid && (
        <div className="">
          <div style={{ height: 300 }}>
            <Image
              // `!` throughout: this modal renders from the house page, whose
              // home carries `basicInfo` and `address`.
              src={basicInfo!.bannerImageUrl!}
              alt=""
              className="object-cover object-top"
            />
          </div>
          <div className="px-2sm py-xl md:px-xl">
            <div className="font-semibold">
              <BulletList
                wrap
                items={[
                  `${formatMoney(listingInfo?.rentAmount)}/mo`,
                  // How to educate about this utils? Make them part of the theme?
                  getFormattedBeds(basicInfo),
                  getBathRoomString(basicInfo!),
                  <span key="sqft">
                    {formatNumber(basicInfo!.areaInSquareFeet!)} ft<sup>2</sup>
                  </span>,
                ]}
              />
            </div>
            <Text variant="p1" className="mb-xl">
              {formatAddress(address!)}
            </Text>
            <Form
              initialValues={{
                interestLevel:
                  option as ResidentLeadInterestUpdateModel['interestLevel'],
                noLongerInMarket: undefined,
              }}
              onSubmit={handleSubmit}
            >
              {({ values }) => {
                return (
                  <div>
                    <Text variant="h3" fontWeight="semibold" className="mb-sm">
                      {attendance === 'Attended'
                        ? 'Is this the home for you?'
                        : 'Are you still interested in this home?'}
                    </Text>
                    <ButtonSelectorField
                      itemHeight="extra-small"
                      name="interestLevel"
                      options={[
                        { label: 'Yes', value: 'Interested' },
                        { label: 'No', value: 'NotInterested' },
                        { label: 'Could Be', value: 'Neutral' },
                      ]}
                    />

                    {values.interestLevel === 'Interested' && (
                      <div className="mt-xl">
                        <Text
                          variant="h3"
                          fontWeight="semibold"
                          className="mb-sm"
                        >
                          Great!{' '}
                          {attendance === 'Attended'
                            ? 'Ready to move forward?'
                            : 'How would you like to view this home?'}
                        </Text>
                        <div className="flex justify-end">
                          <Button
                            size={{ _: 'fluid', md: 'large' }}
                            type="submit"
                          >
                            {attendance !== 'Attended' && 'View Tour Options'}
                            {attendance === 'Attended' &&
                              (applicationStatus === 'NoApplications'
                                ? 'Start An Application'
                                : 'Done')}
                          </Button>
                        </div>
                      </div>
                    )}

                    {values.interestLevel === 'NotInterested' && (
                      <div className="mt-xl">
                        <Text
                          variant="h3"
                          fontWeight="semibold"
                          className="mb-xs"
                        >
                          Thanks for letting us know!
                        </Text>
                        <Text className="mb-sm">
                          Any feedback on this home? What exactly are you
                          looking for?
                        </Text>
                        <div className="mb-xl">
                          <TextField
                            name="interestLevelReason"
                            label="Feedback (Optional)"
                          />
                        </div>
                        <Text
                          variant="h3"
                          fontWeight="semibold"
                          className="mb-sm"
                        >
                          And are you still in the market for a home?
                        </Text>
                        <ButtonSelectorField
                          itemHeight="extra-small"
                          name="noLongerInMarket"
                          // This is weird, but it's not a mistake, the field name is a negation of the question we are asking
                          options={[
                            { label: 'Yes', value: false },
                            { label: 'No', value: true },
                          ]}
                        />

                        {values.noLongerInMarket === true && (
                          <div className="mt-xl">
                            <Text className="mb-xl">
                              {
                                'We hope you found a home to love, and that we can have you as a Belong Resident in the\u00a0future.'
                              }
                            </Text>
                            <div className="flex justify-end">
                              <Button
                                size={{ _: 'fluid', md: 'large' }}
                                type="submit"
                              >
                                Done
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {values.noLongerInMarket === false && (
                      <div className="mt-xl">
                        <Text
                          variant="h3"
                          fontWeight="semibold"
                          className="mb-xl"
                        >
                          {`Well, let's find you a home to love\u00a0then!`}
                        </Text>
                        <div className="flex justify-end">
                          <Button
                            size={{ _: 'fluid', md: 'large' }}
                            type="submit"
                          >
                            View Other Homes
                          </Button>
                        </div>
                      </div>
                    )}

                    {values.interestLevel === 'Neutral' && (
                      <div className="mt-xl">
                        <Text
                          variant="h3"
                          fontWeight="semibold"
                          className="mb-sm"
                        >
                          What would it take to make this the one?
                        </Text>
                        <div className="mb-xl">
                          <TextField
                            name="interestLevelReason"
                            label="Feedback (Optional)"
                          />
                        </div>

                        <div className="mt-xl flex justify-end">
                          <Button
                            size={{ _: 'fluid', md: 'large' }}
                            type="submit"
                          >
                            Submit
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }}
            </Form>
          </div>
        </div>
      )}
    </Modal>
  );
}
